import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

export const AuthCallback = () => {
  const { processGoogleSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (sessionId) {
        const result = await processGoogleSession(sessionId);
        if (result.success) {
          // Check if user needs onboarding
          if (!result.user.onboarding_completed) {
            navigate('/onboarding', { replace: true });
          } else {
            navigate('/discover', { replace: true });
          }
        } else {
          navigate('/login', { replace: true, state: { error: result.error } });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processSession();
  }, [location, processGoogleSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#E07A5F] mx-auto mb-4" />
        <p className="text-[#666B67]">Signing you in...</p>
      </div>
    </div>
  );
};

export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { replace: true, state: { from: location } });
    }
  }, [user, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <Loader2 className="w-12 h-12 animate-spin text-[#E07A5F]" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return children;
};

export const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) {
      navigate('/', { replace: true });
    }
  }, [user, loading, isAdmin, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <Loader2 className="w-12 h-12 animate-spin text-[#E07A5F]" />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return null;
  }

  return children;
};

export const VenueRoute = ({ children }) => {
  const { user, loading, isVenue } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || !isVenue)) {
      navigate('/', { replace: true });
    }
  }, [user, loading, isVenue, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <Loader2 className="w-12 h-12 animate-spin text-[#E07A5F]" />
      </div>
    );
  }

  if (!user || !isVenue) {
    return null;
  }

  return children;
};
